import { Box, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { ReactNode, memo } from 'react';
import { useTranslation } from 'react-i18next';

import { InfoHintIcon } from 'shared/ui/Tooltip';

import {
    catalogProductColumnWidths,
    catalogProductItemHeaderCellSx,
    catalogProductTableHeadSx,
} from './catalogProductTableSpec';

interface CatalogProductTableHeadProps {
    /** e.g. select-all checkbox — sits beside the Item label */
    leading?: ReactNode;
    /** When set, renders the quantity / min-on-hand column with this label */
    quantityColumnLabel?: string;
    quantityColumnTooltip?: string;
    quantityInfoTestClass?: string;
    showPrice?: boolean;
    showStatusColumn?: boolean;
    /** Empty trailing column (row actions) */
    showTrailing?: boolean;
    itemHeaderTestId?: string;
    categoryHeaderTestId?: string;
    uomHeaderTestId?: string;
    quantityHeaderTestId?: string;
    priceHeaderTestId?: string;
}

export const CatalogProductTableHead = memo(
    ({
        leading,
        quantityColumnLabel,
        quantityColumnTooltip,
        quantityInfoTestClass,
        showPrice = false,
        showStatusColumn = false,
        showTrailing = false,
        itemHeaderTestId,
        categoryHeaderTestId,
        uomHeaderTestId,
        quantityHeaderTestId,
        priceHeaderTestId,
    }: CatalogProductTableHeadProps) => {
        const { t } = useTranslation();

        const itemLabel = (
            <Typography variant="caption" fontWeight={600}>
                {t('Item')}
            </Typography>
        );

        return (
            <TableHead sx={catalogProductTableHeadSx}>
                <TableRow>
                    <TableCell
                        data-testid={itemHeaderTestId}
                        sx={catalogProductItemHeaderCellSx}
                    >
                        {leading ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                }}
                            >
                                {leading}
                                {itemLabel}
                            </Box>
                        ) : (
                            itemLabel
                        )}
                    </TableCell>
                    <TableCell
                        data-testid={categoryHeaderTestId}
                        align="left"
                        width={catalogProductColumnWidths.category}
                    >
                        <Typography variant="caption" fontWeight={600}>
                            {t('Category')}
                        </Typography>
                    </TableCell>
                    <TableCell
                        data-testid={uomHeaderTestId}
                        align="center"
                        width={catalogProductColumnWidths.uom}
                    >
                        <Typography variant="caption" fontWeight={600}>
                            {t('UOM')}
                        </Typography>
                    </TableCell>
                    {quantityColumnLabel ? (
                        <TableCell
                            data-testid={quantityHeaderTestId}
                            align="center"
                            width={
                                quantityColumnTooltip
                                    ? catalogProductColumnWidths.defaultQty
                                    : catalogProductColumnWidths.minOnHand
                            }
                        >
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '4px',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                <Typography variant="caption" fontWeight={600}>
                                    {quantityColumnLabel}
                                </Typography>
                                {quantityColumnTooltip ? (
                                    <InfoHintIcon
                                        content={quantityColumnTooltip}
                                        dataTestClass={quantityInfoTestClass}
                                    />
                                ) : null}
                            </Box>
                        </TableCell>
                    ) : null}
                    {showPrice ? (
                        <TableCell
                            data-testid={priceHeaderTestId}
                            align="right"
                        >
                            <Typography variant="caption" fontWeight={600}>
                                {t('Price')}
                            </Typography>
                        </TableCell>
                    ) : null}
                    {showStatusColumn ? <TableCell width="40px" /> : null}
                    {showTrailing ? (
                        <TableCell width={catalogProductColumnWidths.buttons} />
                    ) : null}
                </TableRow>
            </TableHead>
        );
    },
);

CatalogProductTableHead.displayName = 'CatalogProductTableHead';
